/**
 * Os quatro pilares da plataforma.
 *
 * ── Movimento ───────────────────────────────────────────────────────────
 * Segue a identidade declarada em `lib/motion.ts` ("energético com contenção
 * premium"): a curva assinatura `expo.out` e o padrão de entrada `revealUp`,
 * os mesmos do resto do produto. Inventar uma entrada só para esta seção é o
 * que faz telas diferentes parecerem produtos diferentes.
 *
 * Duas camadas, de propósito:
 *   primária    o card sobe 28px e materializa
 *   secundária  o ícone entra depois que o card assenta, com um respiro de
 *               overshoot — é o detalhe que tira o aspecto de "bloco que
 *               apareceu"
 *
 * A camada ambiente foi deixada de fora conscientemente. Quatro cards pulsando
 * ao mesmo tempo violariam a regra de "no máximo um terço dos elementos em
 * movimento simultâneo" e roubariam atenção do conteúdo, que é texto para ler.
 * A vida contínua desta seção mora no hover, que responde a um card por vez.
 *
 * O gatilho é de rolagem e dispara uma vez só: reanimar a cada passagem
 * transforma a seção num letreiro.
 */

import { useRef } from 'react';
import { gsap, useGSAP, revealUp, EASE, DURATION } from '@/lib/motion';

interface Pilar {
  titulo: string;
  texto: string;
  /** `d` do path do ícone, desenhado com stroke em currentColor. */
  icone: string[];
}

const PILARES: Pilar[] = [
  {
    titulo: 'Sorteio 100% auditável',
    texto:
      'Grupos e chaveamentos gerados a partir de uma semente publicada. Qualquer pessoa reproduz o resultado e confere — ninguém escolhe adversário.',
    icone: ['M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
  },
  {
    titulo: 'Tabela sempre em dia',
    texto:
      'Classificação recalculada a cada resultado lançado, com os critérios de desempate do regulamento aplicados na ordem certa.',
    icone: ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7'],
  },
  {
    titulo: 'Estatísticas da EA',
    texto:
      'Artilharia, assistências e notas vêm dos dados oficiais do EA SPORTS FC, coletados e casados com os confrontos da edição.',
    icone: [
      'M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2',
    ],
  },
  {
    titulo: 'Premiação publicada',
    texto:
      'O valor de cada colocação fica visível desde a inscrição, e o pagamento é feito via Pix após o encerramento oficial.',
    icone: ['M2 6h20v12H2z', 'M6 12h12'],
  },
];

export function Pilares() {
  const raiz = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const linhaDoTempo = gsap.timeline({
        scrollTrigger: {
          trigger: raiz.current,
          // 80% da altura: a seção começa a entrar quando já está claramente
          // em cena, não quando espia pela borda de baixo.
          start: 'top 80%',
          // Uma vez. Não é scrub: aqui a rolagem apenas dá a deixa.
          once: true,
        },
      });

      linhaDoTempo
        .add(revealUp('[data-anim="pilar-cabecalho"]', { stagger: 0.08 }))
        // 0.08 entre cards: quatro deles fecham em 240 ms, dentro do teto de
        // meio segundo que mantém o conjunto lendo como um gesto só.
        .add(revealUp('[data-anim="pilar"]', { stagger: 0.08 }), '-=0.55')
        .from(
          '[data-anim="pilar-icone"]',
          {
            scale: 0.6,
            autoAlpha: 0,
            duration: DURATION.standard,
            // Único ponto com overshoot na seção, e mínimo: dá vida ao ícone
            // sem quicar, que é o limite do arquétipo da marca.
            ease: 'back.out(1.6)',
            stagger: 0.08,
          },
          '-=0.75',
        );
    },
    { scope: raiz },
  );

  return (
    <section ref={raiz} className="relative z-10 border-t border-line bg-surface/30 py-24">
      <div className="mx-auto max-w-ucc px-6">
        <div className="max-w-2xl">
          <span data-anim="pilar-cabecalho" className="ucc-eyebrow block">
            Tecnologia & Competitividade
          </span>
          <h2 data-anim="pilar-cabecalho" className="ucc-display mt-2 text-3xl text-paper md:text-5xl">
            Feito por quem joga, para quem quer vencer.
          </h2>
          <p data-anim="pilar-cabecalho" className="mt-4 text-base text-muted md:text-lg">
            Sem planilha manual, sorteio duvidoso ou resultado que demora. A UCC cuida da
            infraestrutura para a organização cuidar do campeonato.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PILARES.map((pilar) => (
            <article
              key={pilar.titulo}
              data-anim="pilar"
              className="ucc-panel group relative overflow-hidden p-6 transition-all duration-[--duration-standard] hover:-translate-y-1 hover:border-gold/30 hover:shadow-[var(--shadow-lift)]"
            >
              <div
                data-anim="pilar-icone"
                className="flex size-11 items-center justify-center rounded-lg border border-gold/30 bg-gold/10 text-gold-ink transition-transform duration-[--duration-standard] ease-[--ease-signature] group-hover:scale-110"
              >
                <svg
                  className="size-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  {pilar.icone.map((d) => (
                    <path key={d} d={d} />
                  ))}
                </svg>
              </div>
              <h3 className="ucc-display mt-5 text-xl text-paper">{pilar.titulo}</h3>
              <p className="mt-2 text-sm text-muted">{pilar.texto}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
